import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

export interface QuantityOption {
  quantity: number;
  unit: string;
  notes?: string;
}

interface SingleSpecQuantityEditorProps {
  specificationId: string;
  specificationName: string;
  options: QuantityOption[];
  onChange: (options: QuantityOption[]) => void;
  readOnly?: boolean;
}

const COMMON_UNITS = [
  'each',
  'piece',
  'box',
  'case',
  'pallet',
  'pound',
  'kilogram',
  'ton',
  'meter',
  'foot',
  'gallon',
  'liter',
  'other'
];

export function SingleSpecQuantityEditor({
  specificationId,
  specificationName,
  options,
  onChange,
  readOnly = false
}: SingleSpecQuantityEditorProps) {
  const addOption = () => {
    if (options.length >= 5) return;
    onChange([...options, { quantity: 0, unit: 'each', notes: '' }]);
  };

  const removeOption = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const updateOption = (
    index: number,
    field: 'quantity' | 'unit' | 'notes',
    value: string | number
  ) => {
    const updatedOptions = [...options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: field === 'quantity' ? Number(value) : value
    };
    onChange(updatedOptions);
  };

  const isOptionValid = (option: QuantityOption) => {
    return option.quantity > 0 && option.unit !== '';
  };

  const hasValidOptions = options.length > 0 && options.every(isOptionValid);

  return (
    <div className="pt-3 border-t space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Quantity Options</Label>
          {!readOnly && options.length === 0 && (
            <Badge variant="destructive" className="text-xs">Required</Badge>
          )}
          {!readOnly && options.length > 0 && !hasValidOptions && (
            <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-300">
              Incomplete
            </Badge>
          )}
          {!readOnly && hasValidOptions && (
            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
              {options.length} option{options.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        {!readOnly && options.length < 5 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addOption}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Option
          </Button>
        )}
      </div>

      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">
          No quantity options added yet. Click "Add Option" to define pricing tiers.
        </p>
      ) : (
        <div className="space-y-2">
          {options.map((option, index) => (
            <div
              key={index}
              className={`p-3 border rounded-lg ${
                !readOnly && !isOptionValid(option) ? 'border-red-300 bg-red-50/50' : 'bg-muted/30'
              }`}
            >
              <div className="grid grid-cols-12 gap-3 items-start">
                <div className="col-span-3">
                  <Label htmlFor={`qty-${specificationId}-${index}`} className="text-xs">
                    Quantity
                  </Label>
                  <Input
                    id={`qty-${specificationId}-${index}`}
                    type="number"
                    min="1"
                    step="any"
                    value={option.quantity || ''}
                    onChange={(e) => updateOption(index, 'quantity', e.target.value)}
                    placeholder="0"
                    disabled={readOnly}
                    className={!isOptionValid(option) && !readOnly ? 'border-red-500' : ''}
                  />
                </div>
                <div className="col-span-3">
                  <Label htmlFor={`unit-${specificationId}-${index}`} className="text-xs">
                    Unit
                  </Label>
                  <Select
                    value={option.unit}
                    onValueChange={(value) => updateOption(index, 'unit', value)}
                    disabled={readOnly}
                  >
                    <SelectTrigger id={`unit-${specificationId}-${index}`}>
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMON_UNITS.map(unit => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-5">
                  <Label htmlFor={`notes-${specificationId}-${index}`} className="text-xs">
                    Notes (optional)
                  </Label>
                  <Input
                    id={`notes-${specificationId}-${index}`}
                    type="text"
                    value={option.notes || ''}
                    onChange={(e) => updateOption(index, 'notes', e.target.value)}
                    placeholder=""
                    disabled={readOnly}
                  />
                </div>
                {!readOnly && (
                  <div className="col-span-1 pt-6">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOption(index)}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!readOnly && options.length >= 5 && (
        <p className="text-xs text-muted-foreground">
          Maximum of 5 quantity options reached.
        </p>
      )}
    </div>
  );
}
